import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, CurrencyInput, ImageUpload, Modal, TextInput } from '@kardume/ui';
import {
  FEATURED_ITEM_DESCRIPTION_MAX_LENGTH,
  FEATURED_ITEM_NAME_MAX_LENGTH,
  FEATURED_ITEM_PRICE_MAX,
} from '@kardume/types';
import type { MerchantFeaturedItemDto } from '@kardume/types';
import { uploadMerchantImage } from '../../utils/imageUpload';
import {
  emptyFeaturedItemForm,
  hasFeaturedItemErrors,
  toFeaturedItemForm,
  toFeaturedItemRequest,
  validateFeaturedItem,
} from './featuredItemForm';
import type { FeaturedItemFormState } from './featuredItemForm';
import type { MerchantFeaturedItemRequestDto } from '@kardume/types';

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

/**
 * Foto e nome lado a lado.
 *
 * A moldura compacta do `ImageUpload` tem 88px de altura e, em 4:3, 117px de
 * largura - pedido recente do Leonardo para que campo de imagem não ocupe a
 * tela como se fosse o campo principal. Do lado dela cabem o nome e o preço,
 * que são o que o lojista de fato veio digitar. Abaixo de 520px a linha vira
 * coluna: a moldura mais o campo de nome não cabem lado a lado no celular.
 */
const PhotoRow = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 1rem;
  align-items: start;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const StackedFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CharCount = styled.span<{ $over: boolean }>`
  display: block;
  margin-top: 0.3rem;
  font-size: 0.75rem;
  text-align: right;
  color: ${(p) => (p.$over ? p.theme.error : p.theme.textMuted)};
`;

const FieldError = styled.span`
  display: block;
  margin-top: 0.35rem;
  font-size: 0.75rem;
  color: ${(p) => p.theme.error};
`;

const TextArea = styled.textarea<{ $error?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  min-height: 76px;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
  font-family: inherit;
  resize: vertical;
  color: ${(p) => p.theme.textPrimary};
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => (p.$error ? p.theme.error : p.theme.border)};
  border-radius: ${(p) => p.theme.radius};

  &::placeholder {
    color: ${(p) => p.theme.textMuted};
  }
  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
    box-shadow: 0 0 0 3px ${(p) => p.theme.primary}22;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
`;

interface FeaturedItemFormModalProps {
  isOpen: boolean;
  /** `undefined` = cadastrar; um item = editar aquele item. */
  item?: MerchantFeaturedItemDto;
  saving: boolean;
  onSubmit: (payload: MerchantFeaturedItemRequestDto) => void;
  onClose: () => void;
}

/**
 * Cadastro e edição de um item em destaque.
 *
 * **Sem `<form>` por dentro, de propósito.** O `ProfilePage` inteiro já vive
 * dentro de um `<form>`, e um `<form>` aninhado é HTML inválido; o `Modal`
 * escapa por portal no DOM, mas o evento continua subindo pela árvore do React
 * e cairia no `handleSubmit` do perfil. Pelo mesmo motivo todo botão daqui é
 * `type="button"`: sem isso, o padrão `submit` faria "Cancelar" salvar o
 * perfil inteiro.
 */
export const FeaturedItemFormModal: React.FC<FeaturedItemFormModalProps> = ({
  isOpen,
  item,
  saving,
  onSubmit,
  onClose,
}) => {
  const [form, setForm] = useState<FeaturedItemFormState>(emptyFeaturedItemForm);
  /**
   * Erro só depois da primeira tentativa de salvar - mesma regra do perfil:
   * pintar de vermelho um formulário recém-aberto acusa o lojista de algo que
   * ele ainda não fez.
   */
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(item ? toFeaturedItemForm(item) : emptyFeaturedItemForm());
    setShowErrors(false);
  }, [isOpen, item]);

  const errors = useMemo(() => validateFeaturedItem(form), [form]);
  const errorOf = (field: keyof FeaturedItemFormState) => (showErrors ? errors[field] : undefined);

  const update = <K extends keyof FeaturedItemFormState>(key: K, value: FeaturedItemFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    setShowErrors(true);
    if (hasFeaturedItemErrors(validateFeaturedItem(form))) return;
    onSubmit(toFeaturedItemRequest(form));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={item ? 'Editar item em destaque' : 'Novo item em destaque'}
      width="600px"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleSave} isLoading={saving}>
            {item ? 'Salvar item' : 'Adicionar item'}
          </Button>
        </>
      }
    >
      <Fields>
        <PhotoRow>
          <ImageUpload
            label="Foto"
            variant="featuredItem"
            compact
            callToAction="Enviar foto"
            value={form.imageUrl}
            onChange={(url) => update('imageUrl', url)}
            onUpload={uploadMerchantImage('GALLERY')}
            disabled={saving}
            helpText="Opcional · 4:3"
          />
          <StackedFields>
            <div>
              <TextInput
                label="Nome"
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="Ex.: Cappuccino da casa"
                maxLength={FEATURED_ITEM_NAME_MAX_LENGTH}
                error={errorOf('name')}
                disabled={saving}
                required
              />
            </div>
            <div>
              {/*
                O preço é o campo mais delicado do card. O `allowEmpty` faz o
                campo poder REALMENTE ficar vazio: sem ele, apagar com Backspace
                trava em "0,00" e o lojista sem preço fixo acabaria gravando um
                zero. Vazio aqui vira `price: null` no corpo do pedido
                (`toFeaturedItemRequest`), e é o `null` que faz o site e o app
                não desenharem linha de preço nenhuma.
              */}
              <CurrencyInput
                label="Preço"
                allowEmpty
                max={FEATURED_ITEM_PRICE_MAX}
                value={form.price}
                onChange={(value) => update('price', value)}
                placeholder="deixe em branco"
                helpText="Sem preço fixo? Deixe em branco - o item aparece sem preço, e não como R$ 0,00."
                error={errorOf('price')}
                disabled={saving}
              />
            </div>
          </StackedFields>
        </PhotoRow>

        <div>
          <Label htmlFor="featured-item-description">Descrição</Label>
          <TextArea
            id="featured-item-description"
            value={form.description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              update('description', e.target.value)
            }
            placeholder="Ex.: com arte na espuma, grãos torrados aqui"
            $error={!!errorOf('description')}
            disabled={saving}
          />
          <CharCount $over={form.description.trim().length > FEATURED_ITEM_DESCRIPTION_MAX_LENGTH}>
            {form.description.trim().length}/{FEATURED_ITEM_DESCRIPTION_MAX_LENGTH}
          </CharCount>
          {errorOf('description') && <FieldError>{errorOf('description')}</FieldError>}
        </div>
      </Fields>
    </Modal>
  );
};

export default FeaturedItemFormModal;
